import DigiobcanApi, { type NotificationKind } from '../../api/DigiobcanApi';
import FormSwitchControl from '../../components/form/FormSwitchControl';
import { meGetNotificationsSettings } from '../../features/queries/meGetNotificationsSettings';
import { meNotificationsEmailStatus } from '../../features/queries/meNotificationsEmailStatus';
import { meNotificationsWebpushStatus } from '../../features/queries/meNotificationsWebpushStatus';
import { useQ } from '../../lib/query';
import { useLogger } from '../../providers/LoggerProvider';
import { useWebPush } from '../../providers/WebPushProvider';
import { __, toastError, toastSuccess } from '../../utils';
import NotificationKinds from './NotificationKinds';
import { useAuth } from '@presov.sk/presov-auth-react';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { Form } from 'react-final-form';

type Interval = 'daily' | 'weekly' | 'monthly' | null;

type Values = {
  enabled: boolean;
  interval: Interval;
  types?: ('webpush' | 'email')[];
  muted_kinds: NotificationKind[];
};

const INTERVAL_OPTIONS: Array<{ value: Interval; label: string }> = [
  { value: null, label: __('Bez súhrnu') },
  { value: 'daily', label: __('Denne') },
  { value: 'weekly', label: __('Týždenne') },
  { value: 'monthly', label: __('Mesačne') },
];

/** Vybratý stav nesie plná farba rámu, nie len podklad - kvôli kontrastu. */
const ChoiceChip: React.FC<{
  label: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ label, selected, disabled, onClick }) => (
  <Box
    role='radio'
    aria-checked={selected}
    aria-disabled={disabled}
    tabIndex={disabled ? -1 : 0}
    onClick={() => !disabled && onClick()}
    onKeyDown={(event) => {
      if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onClick();
      }
    }}
    sx={(theme) => ({
      display: 'flex',
      alignItems: 'center',
      height: 38,
      px: 2,
      borderRadius: 1,
      fontSize: 14,
      fontWeight: 600,
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      border: '1px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      color: selected ? 'primary.dark' : 'text.secondary',
      bgcolor: selected
        ? alpha(theme.palette.primary.main, 0.06)
        : 'background.paper',
      '&:hover': disabled ? undefined : { borderColor: 'primary.main' },
    })}
  >
    {label}
  </Box>
);

const NotificationSettings: React.FC = () => {
  const { authUser } = useAuth();
  const { logger } = useLogger();
  const { subscribe, unsubscribe, getSubscription } = useWebPush();

  const [webpushEndpoint, setWebpushEndpoint] = React.useState('');
  const [hasCreatedEndpoint, setHasCreatedEndpoint] = React.useState<
    'loading' | 'has' | 'missing'
  >('loading');

  const {
    data: dataNotificationsSettings,
    isError: isErrorNotificationsSettings,
  } = useQ(meGetNotificationsSettings);

  const {
    data: emailStatus,
    isLoading: isLoadingEmailStatus,
    isError: isErrorEmailStatus,
  } = useQ(meNotificationsEmailStatus);

  const {
    data: webpushStatus,
    isLoading: isLoadingWebpush,
    isError: isErrorWebpush,
  } = useQ(
    meNotificationsWebpushStatus,
    {
      endpoint: webpushEndpoint,
    },
    {
      enabled: !!webpushEndpoint && hasCreatedEndpoint === 'has',
    },
  );

  const handleUpdateNotificationsSettings = async (values: Values) => {
    try {
      const { enabled, interval, types, muted_kinds } = values;

      await Promise.all([
        (async () => {
          try {
            await DigiobcanApi.meUpdateNotificationsSettings(
              {},
              { enabled, interval, muted_kinds },
            );
          } catch (e) {
            toastError(__('Nepodarilo sa uložiť nastavenia notifikácií'));
            logger.error('Failed to update notifications settings', {
              action: 'updateNotificationsSettings',
              error: e,
              data: {
                ...values,
              },
            });
          }
        })(),
        (async () => {
          if (!enabled) return;

          try {
            if (types.includes('webpush')) {
              await subscribe({ askNotifications: true });
            } else {
              await unsubscribe();
            }
          } catch (e) {
            toastError(__('Nepodarilo sa uložiť nastavenia Push notifikácií'));
            logger.error('Failed to update webpush settings', {
              action: 'updateWebPushSettings',
              error: e,
              data: {
                ...values,
              },
            });
          }
        })(),
        (async () => {
          if (!enabled) return;

          if (authUser?.email) {
            try {
              if (types.includes('email')) {
                await DigiobcanApi.meNotificationsEmailSubscribe();
              } else {
                await DigiobcanApi.meNotificationsEmailUnsubscribe();
              }
            } catch (e) {
              toastError(
                __('Nepodarilo sa uložiť nastavenia Email notifikácií'),
              );
              logger.error('Failed to update email settings', {
                action: 'updateEmailSettings',
                error: e,
                data: {
                  ...values,
                },
              });
            }
          }
        })(),
      ]);

      toastSuccess(__('Zmena prebehla úspešne'));
    } catch (e) {
      toastError(__('pri ukladaní nastavení notifikácií sa vyskytla chyba.'));
      logger.error('Error updating notifications settings', {
        action: 'updateNotificationsSettings',
        error: e,
        data: {
          ...values,
        },
      });
    }
  };

  React.useEffect(() => {
    if (isErrorNotificationsSettings) {
      toastError(__('Chyba pri načítaní nastavení notifikácií'));
    }
  }, [isErrorNotificationsSettings]);

  React.useEffect(() => {
    if (isErrorEmailStatus) {
      toastError(__('Chyba pri načítaní stavu Email notifikácií'));
    }
  }, [isErrorEmailStatus]);

  React.useEffect(() => {
    if (isErrorWebpush) {
      toastError(__('Chyba pri načítaní stavu WebPush notifikácií'));
    }
  }, [isErrorWebpush]);

  React.useEffect(() => {
    (async () => {
      const subscription = await getSubscription();
      if (subscription) {
        setWebpushEndpoint(subscription.endpoint);
        setHasCreatedEndpoint('has');
      } else {
        setWebpushEndpoint('');
        setHasCreatedEndpoint('missing');
      }
    })();
  }, [getSubscription]);

  // Stabilná referencia - `react-final-form` pri novom objekte formulár
  // reinicializuje, a s poľom `muted_kinds` by sa to dialo pri každom renderi
  // a prepínače druhov by sa vracali (detail v `.docs/vyvoj/formulare.md`).
  const initialValues = React.useMemo<Values>(
    () => ({
      enabled: dataNotificationsSettings?.enabled ?? false,
      interval: dataNotificationsSettings?.interval ?? null,
      muted_kinds: dataNotificationsSettings?.muted_kinds ?? [],
      types:
        !isLoadingEmailStatus && !isLoadingWebpush
          ? [
              ...(authUser?.email && emailStatus
                ? (['email'] as ['email'])
                : []),
              ...(webpushStatus ? (['webpush'] as ['webpush']) : []),
            ]
          : [],
    }),
    [
      dataNotificationsSettings,
      isLoadingEmailStatus,
      isLoadingWebpush,
      authUser?.email,
      emailStatus,
      webpushStatus,
    ],
  );

  return (
    <Stack gap={2} height={'100%'}>
      <Form<Values>
        initialValues={initialValues}
        onSubmit={handleUpdateNotificationsSettings}
      >
        {({ handleSubmit, submitting, values, form }) => {
          const channels = [
            ...(authUser?.email
              ? [
                  {
                    value: 'email' as const,
                    label: __('Email'),
                    note: authUser.email,
                  },
                ]
              : []),
            {
              value: 'webpush' as const,
              label: __('Push notifikácie'),
              // Nie „Mobilná aplikácia" - natívnu appku mesto nemá, push
              // chodí do prehliadača a do appky nainštalovanej z webu (PWA).
              note: __('Vo webovej aplikácii a prehliadači'),
            },
          ];

          const isChannelsDisabled =
            !values.enabled || isLoadingEmailStatus || isLoadingWebpush;

          const toggleChannel = (value: 'email' | 'webpush') => {
            const selected = values.types || [];
            form.change(
              'types',
              selected.includes(value)
                ? selected.filter((type) => type !== value)
                : [...selected, value],
            );
          };

          return (
            <form
              onSubmit={handleSubmit}
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
              }}
            >
              <Stack sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {/* Hlavný vypínač */}
                <Stack
                  direction='row'
                  alignItems='center'
                  gap={2}
                  sx={{
                    pb: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack gap={0.25} flex={1}>
                    <Typography variant='body1' fontWeight={600}>
                      {__('Aktívne notifikácie')}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {__('Vypnutím prestanete dostávať všetky upozornenia.')}
                    </Typography>
                  </Stack>
                  <FormSwitchControl name='enabled' />
                </Stack>

                {/* O čom chcem byť notifikovaný (moj-presov#28) */}
                <Stack
                  gap={1.5}
                  sx={{
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack gap={0.25}>
                    <Typography variant='body1' fontWeight={600}>
                      {__('O čom chcem byť notifikovaný')}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {__(
                        'Vypnuté upozornenia nedostanete vôbec - ani e-mailom, ani v zvončeku.',
                      )}
                    </Typography>
                  </Stack>
                  <NotificationKinds
                    muted={values.muted_kinds}
                    // Zamknuté ako súhrn a kanály - pri vypnutých
                    // notifikáciách sa nevyberá, o čom ich dostávať.
                    disabled={!values.enabled}
                    onChange={(muted) => form.change('muted_kinds', muted)}
                  />
                </Stack>

                {/* Súhrn notifikácií */}
                <Stack
                  gap={1.5}
                  sx={{
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant='body1' fontWeight={600}>
                    {__('Súhrn notifikácií')}
                  </Typography>
                  <Stack
                    direction='row'
                    gap={1}
                    flexWrap='wrap'
                    role='radiogroup'
                  >
                    {INTERVAL_OPTIONS.map((option) => (
                      <ChoiceChip
                        key={String(option.value)}
                        label={option.label}
                        selected={values.interval === option.value}
                        disabled={!values.enabled}
                        onClick={() => form.change('interval', option.value)}
                      />
                    ))}
                  </Stack>
                </Stack>

                {/* Spôsob doručovania */}
                <Stack gap={1.5} sx={{ py: 2 }}>
                  <Typography variant='body1' fontWeight={600}>
                    {__('Spôsob doručovania')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 260px))',
                      },
                    }}
                  >
                    {channels.map((channel) => {
                      const selected = (values.types || []).includes(
                        channel.value,
                      );

                      return (
                        <Stack
                          key={channel.value}
                          direction='row'
                          alignItems='center'
                          gap={1}
                          onClick={() =>
                            !isChannelsDisabled && toggleChannel(channel.value)
                          }
                          sx={(theme) => ({
                            px: 1.5,
                            py: 1.25,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: selected ? 'primary.main' : 'divider',
                            bgcolor: selected
                              ? alpha(theme.palette.primary.main, 0.06)
                              : 'background.paper',
                            cursor: isChannelsDisabled ? 'default' : 'pointer',
                            opacity: isChannelsDisabled ? 0.5 : 1,
                          })}
                        >
                          <Checkbox
                            checked={selected}
                            disabled={isChannelsDisabled}
                            inputProps={{ 'aria-label': channel.label }}
                            sx={{ p: 0 }}
                          />
                          <Stack gap={0}>
                            <Typography variant='body2' fontWeight={600}>
                              {channel.label}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              {channel.note}
                            </Typography>
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Box>
                </Stack>
              </Stack>

              {/* Uloženie */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                gap={1.5}
                sx={{
                  flexShrink: 0,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ flex: 1 }}
                >
                  {__('Zmeny sa uložia pre všetky zariadenia.')}
                </Typography>
                <Button
                  variant='contained'
                  type='submit'
                  loading={submitting}
                  sx={{ width: { xs: '100%', sm: 'fit-content' } }}
                >
                  {__('Uložiť zmeny')}
                </Button>
              </Stack>
            </form>
          );
        }}
      </Form>
    </Stack>
  );
};

export default NotificationSettings;
